import { useState, useEffect, useRef } from "react";
import {
  tableCellClasses,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import AxiosInstance from "../../services/AxiosInstance";
import {
  REPORT_BUILDER_URL,
  ASSET_ITEM_URL,
  DEPARTMETNS_URL,
  ORG_URL,
  IMPACTS_URL,
  PRIORITIES_URL,
  REQUEST_TYPE_URL,
  STATUS_URL,
  RESOLVER_GROUP_URL,
  RESOLVER_BY_RG_URL,
  REPORT_DOWNLOAD_BUILDER_URL,
} from "../../Constants/Constants";
import FormControl from "@mui/material/FormControl";
import { fetchData } from "../../services/UtilService";
import Button from "@mui/material/Button";
import { format, parseISO } from "date-fns";
import { styled } from "@mui/material/styles";
import ReportScheduleDialog from "./ReportScheduleDialog";
import { createTheme, ThemeProvider, useTheme } from "@mui/material";
import AppTableView from "../AppTableView";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { is } from "date-fns/locale";

function ReportBuilder() {
  const globaltheme = useTheme();
  const theme = createTheme({
    ...globaltheme,
    components: {
      MuiSelect: {
        defaultProps: {
          sx: {
            borderRadius: "8px",
            // Prevent italic placeholder/label
            "& em": {
              fontStyle: "normal",
            },
          },
        },
      },
      MuiButton: {
        defaultProps: {
          sx: {
            marginTop: 2,
            marginBottom: 2,
            display: "block",
          },
        },
      },
    },
  });

  // Grid view
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.common.black,
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    "&:last-child td, &:last-child th": {
      border: 0,
    },
  }));

  const [tickets, setTickets] = useState([]);
  const [page, setPage] = useState(0); // Important: 0-based
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [totalElements, setTotalElements] = useState(0);

  const initialized = useRef(false);

  const [statusFilter, setStatusFilter] = useState(0);
  const [organizationFilter, setOrganizationFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [resolverGroupFilter, setResolverGroupFilter] = useState("");
  const [resolverFilter, setResolverFilter] = useState("");
  const [requestTypeFilter, setRequestTypeFilter] = useState("");
  const [assetItemFilter, setAssetItemFilter] = useState("");

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");

  const [statuses, setStatuses] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [requestTypes, setRequestTypes] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [resolverGroups, setResolverGroups] = useState([]);
  const [assetItems, setAssetItems] = useState([]);
  const [resolvers, setResolvers] = useState([]);
  const [organizations, setOrganizations] = useState([]);

  const [paginationData, setPaginationData] = useState({
    page: 0,
    rowsPerPage: 15,
  });
  const [loading, setLoading] = useState(false);

  const setPaginationParams = ({ page, pageSize }) => {
    setPaginationData((prevState) => {
      const { page: prevPage, rowsPerPage: prevPageSize } = prevState || {};

      if (!page && !pageSize) {
        return {
          page: 0,
          rowsPerPage: prevPageSize || 20,
        };
      }

      if (prevPageSize === pageSize && prevPage === page) {
        return prevState;
      }

      if (prevPageSize !== pageSize) {
        return {
          page: 0,
          rowsPerPage: pageSize,
        };
      }

      return {
        page,
        rowsPerPage: pageSize,
      };
    });
  };

  const fetchTickets = () => {
    console.log("global theme:", globaltheme);
    setLoading(true);
    const { page, rowsPerPage } = paginationData;
    AxiosInstance.post(REPORT_BUILDER_URL, {
      page: page,
      size: rowsPerPage,
      statusId: statusFilter,
      priorityId: priorityFilter,
      requestTypeId: requestTypeFilter,
      assetItemId: assetItemFilter,
      resolverGroupId: resolverGroupFilter,
      resolverId: resolverFilter,
      startDate: startDate ? format(startDate, "yyyy-MM-dd") : null,
      endDate: endDate ? format(endDate, "yyyy-MM-dd") : null,
      orgId: organizationFilter,
      search: searchTerm,
    })
      .then((response) => {
        setTickets(response.data.content);
        setTotalElements(response.data.totalElements);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      fetchData(ORG_URL)
        .then((data) => setOrganizations(data.data))
        .catch((err) => console.log("Failed to fetch data"));
      fetchData(REQUEST_TYPE_URL)
        .then((data) => setRequestTypes(data))
        .catch((err) => console.log("Failed to fetch data"));
      fetchData(STATUS_URL)
        .then((data) => setStatuses(data))
        .catch((err) => console.log("Failed to fetch data"));
      fetchData(ASSET_ITEM_URL)
        .then((data) => setAssetItems(data.data))
        .catch((err) => console.log("Failed to fetch data"));
      fetchData(PRIORITIES_URL)
        .then((data) => setPriorities(data.data))
        .catch((err) => console.log("Failed to fetch data"));
      fetchData(RESOLVER_GROUP_URL)
        .then((data) => setResolverGroups(data.data))
        .catch((err) => console.log("Failed to fetch data"));
    }

    fetchTickets();
  }, [paginationData]);

  const handlePriorityChange = (event) => {
    setPriorityFilter(event.target.value);
  };

  const handleResolverGroupChange = (event) => {
    setResolverGroupFilter(event.target.value);
    console.log("resolver url:" + RESOLVER_BY_RG_URL + event.target.value);
    AxiosInstance.get(`${RESOLVER_BY_RG_URL}/${event.target.value}`).then(
      (response) => setResolvers(response.data)
    );
  };

  const handleResolverChange = (event) => {
    setResolverFilter(event.target.value);
  };

  const handleOrgChange = (event) => {
    setOrganizationFilter(event.target.value);
  };

  const handleRequestTypeChange = (event) => {
    setRequestTypeFilter(event.target.value);
  };

  const handleAssetItemChange = (event) => {
    setAssetItemFilter(event.target.value);
  };

  const handleStartDateChange = (date) => {
    // const isoDate = date ? format(date, "yyyy-MM-dd") : null;
    // console.log("Selected Date:", date);
    setStartDate(date);
  };

  const handleEndDateChange = (date) => {
    // const isoDate = date ? format(date, "yyyy-MM-dd") : null;
    // console.log("Selected Date:", date);
    setEndDate(date);
  };

  const handleLoadResults = () => {
    setPage(0);
    fetchTickets();
  };

  const handleReset = () => {
    setOrganizationFilter("");
    setRequestTypeFilter("");
    setPriorityFilter("");
    setAssetItemFilter("");
    setResolverGroupFilter("");
    setResolverFilter("");
    setStartDate(null);
    setEndDate(null);

    setPage(0);
    fetchTickets();
  };

  const handleExportCSV = () => {
    const headers = [
      "Organization",
      "ID",
      "Subject",
      "Impact",
      "Urgency",
      "Ticket Category",
      "Request Type",
      "Asset Item",
      "Resolver Group",
      "Resolver",
      "Status",
      "Priority",
      "Creatd By",
      "Created On",
      "Last Modified On",
    ];

    AxiosInstance.post(REPORT_DOWNLOAD_BUILDER_URL, {
      page: page,
      size: rowsPerPage,
      statusId: statusFilter,
      priorityId: priorityFilter,
      requestTypeId: requestTypeFilter,
      assetItemId: assetItemFilter,
      resolverGroupId: resolverGroupFilter,
      resolverId: resolverFilter,
      startDate: startDate ? format(startDate, "yyyy-MM-dd") : null,
      endDate: endDate ? format(endDate, "yyyy-MM-dd") : null,
      orgId: organizationFilter,
      search: searchTerm,
    })
      .then((response) => {
        var reportData = response.data;
        const rows = reportData.map((ticket) => [
          ticket?.organization,
          ticket?.id,
          ticket?.subject,
          ticket?.impact,
          ticket?.urgency,
          ticket?.ticketCategory,
          ticket?.requestType,
          ticket?.assetItem,
          ticket?.resolverGroup,
          ticket?.resolver,
          ticket?.status,
          ticket?.priority,
          ticket?.createdBy,
          //new Date(ticket.createdOn).toLocaleString()
          format(new Date(ticket.createdOn), "dd-MM-yyyy").toLocaleString(),
          format(new Date(ticket.modifiedOn), "dd-MM-yyyy").toLocaleString(),
        ]);

        let csvContent =
          "data:text/csv;charset=utf-8," +
          headers.join(",") +
          "\n" +
          rows.map((e) => e.join(",")).join("\n");

        const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `tickets_report_${timestamp}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  return (
    <>
      <ThemeProvider theme={theme}>
        <div style={{ padding: "20px 20px 0px 20px", width: "100%" }}>
          {/* Filters */}
          <Grid container spacing={2} sx={{ mb: 2, width: "100%" }}>
            <Grid item xs={12} md={3}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  fontWeight: 500,
                  marginLeft: "10px",
                }}
              >
                Organization
              </Typography>
              <FormControl
                size="small"
                variant="outlined"
                sx={{
                  m: 1,
                  minWidth: 200,
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    height: 32,
                    "& .MuiSelect-select": {
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      height: "100%",
                    },
                  },
                }}
              >
                <Select
                  labelId="org-select-label"
                  id="org-select"
                  value={organizationFilter || ""}
                  onChange={handleOrgChange}
                  displayEmpty
                  renderValue={(selected) => {
                    const selectedOrg = (organizations || []).find(
                      (org) => org.id === selected
                    );
                    return selectedOrg ? (
                      selectedOrg.name
                    ) : (
                      <em>Select Organization</em>
                    );
                  }}
                >
                  <MenuItem value="">
                    <em>Select Organization</em>
                  </MenuItem>
                  {(organizations || []).map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  fontWeight: 500,
                  marginLeft: "10px",
                }}
              >
                Request Type
              </Typography>
              <FormControl
                size="small"
                variant="outlined"
                sx={{
                  m: 1,
                  minWidth: 150,
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    height: 32,
                    "& .MuiSelect-select": {
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      height: "100%",
                    },
                  },
                }}
              >
                <Select
                  labelId="request-type-select-label"
                  id="request-type-select"
                  value={requestTypeFilter || ""}
                  onChange={handleRequestTypeChange}
                  displayEmpty
                  renderValue={(selected) => {
                    const selectedType = (requestTypes || []).find(
                      (rt) => rt.id === selected
                    );
                    return selectedType ? (
                      selectedType.name
                    ) : (
                      <em>Select Request Type</em>
                    );
                  }}
                >
                  <MenuItem value="">
                    <em>Select Request Type</em>
                  </MenuItem>
                  {(requestTypes || []).map((rt) => (
                    <MenuItem key={rt.id} value={rt.id}>
                      {rt.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  fontWeight: 500,
                  marginLeft: "10px",
                }}
              >
                Priority
              </Typography>
              <FormControl
                size="small"
                variant="outlined"
                sx={{
                  m: 1,
                  minWidth: 150,
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    height: 32,
                    "& .MuiSelect-select": {
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      height: "100%",
                    },
                  },
                }}
              >
                <Select
                  labelId="priority-select-label"
                  id="priority-select"
                  value={priorityFilter || ""}
                  onChange={handlePriorityChange}
                  displayEmpty
                  renderValue={(selected) => {
                    const selectedPriority = (priorities || []).find(
                      (prty) => prty.id === selected
                    );
                    return selectedPriority ? (
                      selectedPriority.name
                    ) : (
                      <em>Select Priority</em>
                    );
                  }}
                >
                  <MenuItem value="">
                    <em>Select Priority</em>
                  </MenuItem>
                  {(priorities || []).map((prty) => (
                    <MenuItem key={prty.id} value={prty.id}>
                      {prty.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  fontWeight: 500,
                  marginLeft: "10px",
                }}
              >
                Asset Item
              </Typography>
              <FormControl
                size="small"
                variant="outlined"
                sx={{
                  m: 1,
                  minWidth: 150,
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    height: 32,
                    "& .MuiSelect-select": {
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      height: "100%",
                    },
                  },
                }}
              >
                <Select
                  labelId="asset-item-select-label"
                  id="asset-item-select"
                  value={assetItemFilter || ""}
                  onChange={handleAssetItemChange}
                  displayEmpty
                  renderValue={(selected) => {
                    const selectedAsset = (assetItems || []).find(
                      (ai) => ai.id === selected
                    );
                    return selectedAsset ? (
                      selectedAsset.name
                    ) : (
                      <em>Select Asset Item</em>
                    );
                  }}
                >
                  <MenuItem value="">
                    <em>Select Asset Item</em>
                  </MenuItem>
                  {(assetItems || []).map((ai) => (
                    <MenuItem key={ai.id} value={ai.id}>
                      {ai.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  fontWeight: 500,
                  marginLeft: "10px",
                }}
              >
                Resolver Group
              </Typography>
              <FormControl
                size="small"
                variant="outlined"
                sx={{
                  m: 1,
                  minWidth: 150,
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    height: 32,
                    "& .MuiSelect-select": {
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      height: "100%",
                    },
                  },
                }}
              >
                <Select
                  labelId="resolver-group-select-label"
                  id="resolver-group-select"
                  value={resolverGroupFilter || ""}
                  onChange={handleResolverGroupChange}
                  displayEmpty
                  renderValue={(selected) => {
                    const selectedGroup = (resolverGroups || []).find(
                      (rg) => rg.id === selected
                    );
                    return selectedGroup ? (
                      selectedGroup.name
                    ) : (
                      <em>Select Resolver Group</em>
                    );
                  }}
                >
                  <MenuItem value="">
                    <em>Select Resolver Group</em>
                  </MenuItem>
                  {(resolverGroups || []).map((rg) => (
                    <MenuItem key={rg.id} value={rg.id}>
                      {rg.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* </Grid>

          <Grid container spacing={2} sx={{ mb: 2, width: "100%" }}> */}
            <Grid item xs={12} md={3}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  fontWeight: 500,
                  marginLeft: "10px",
                }}
              >
                Resolver
              </Typography>
              <FormControl
                size="small"
                variant="outlined"
                sx={{
                  m: 1,
                  minWidth: 150,
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    height: 32,
                    "& .MuiSelect-select": {
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      height: "100%",
                    },
                  },
                }}
              >
                <Select
                  labelId="resolver-select-label"
                  id="resolver-select"
                  value={resolverFilter || ""}
                  onChange={handleResolverChange}
                  displayEmpty
                  renderValue={(selected) => {
                    const selectedResolver = (resolvers || []).find(
                      (res) => res.id === selected
                    );
                    return selectedResolver ? (
                      selectedResolver.user?.name || selectedResolver.name
                    ) : (
                      <em>Select Resolver</em>
                    );
                  }}
                >
                  <MenuItem value="">
                    <em>Select Resolver</em>
                  </MenuItem>
                  {(resolvers || []).map((res) => (
                    <MenuItem key={res.id} value={res.id}>
                      {res.user?.name || res.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* Start Date */}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={startDate}
                onChange={handleStartDateChange}
                label="Start Date"
                views={["year", "month", "day"]}
              />
            </LocalizationProvider>
            {/* End Date */}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={endDate}
                onChange={handleEndDateChange}
                label="End Date"
                views={["year", "month", "day"]}
              />
            </LocalizationProvider>
            <Grid
              item
              xs={12}
              md={3}
              style={{ marginTop: "auto", marginBottom: "7px" }}
            >
              {/* <ReportScheduleDialog /> */}

              <Button
                variant="outlined"
                size="small"
                onClick={handleReset}
                style={{ float: "right", margin: "2px" }}
              >
                Clear
              </Button>

              <Button
                variant="outlined"
                size="small"
                onClick={handleExportCSV}
                style={{ float: "right", margin: "2px" }}
              >
                Export CSV
              </Button>

              <Button
                variant="outlined"
                size="small"
                onClick={handleLoadResults}
                style={{ float: "right", margin: "2px" }}
              >
                Load
              </Button>
            </Grid>
          </Grid>
        </div>
      </ThemeProvider>
      {/* App Table View */}
      <div style={{ padding: "0 20px 20px 20px", width: "100%" }}>
        {loading ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="300px"
            mt={4}
            mb={4}
          >
            <CircularProgress size={48} thickness={4} color="primary" />
            <Box mt={2} color="text.secondary" fontSize={16}>
              Loading tickets, please wait...
            </Box>
          </Box>
        ) : (
          <AppTableView
            rows={tickets}
            cols={[
              {
                field: "organization",
                headerName: "Organization",
                flex: 1,
                renderCell: (params) => {
                  return params.row?.organization || "";
                },
              },
              { field: "id", headerName: "ID", flex: 1 },
              {
                field: "requestType.name",
                headerName: "Request Type",
                flex: 1,
                renderCell: (params) => {
                  return params.row?.requestType || "";
                },
              },
              {
                field: "subject",
                headerName: "Subject",
                flex: 1,
                renderCell: (params) => {
                  return params.row?.subject || "";
                },
              },
              {
                field: "impact",
                headerName: "Impact",
                flex: 1,
                renderCell: (params) => {
                  return params.row?.impact || "";
                },
              },
              {
                field: "urgency",
                headerName: "Urgency",
                flex: 1,
                renderCell: (params) => {
                  return params.row?.urgency || "";
                },
              },
              {
                field: "ticketCategory",
                headerName: "Ticket Category",
                flex: 1,
                renderCell: (params) => {
                  return params.row?.ticketCategory || "";
                },
              },
              {
                field: "assetItem",
                headerName: "Asset Item",
                flex: 1,
                renderCell: (params) => {
                  return params.row?.assetItem || "";
                },
              },
              {
                field: "resolverGroup",
                headerName: "Resolver Group",
                flex: 1,
                renderCell: (params) => {
                  return params.row?.resolverGroup || "";
                },
              },
              {
                field: "resolver",
                headerName: "Resolver",
                flex: 1,
                renderCell: (params) => {
                  return params.row?.resolver || "";
                },
              },
              {
                field: "status",
                headerName: "Status",
                flex: 1,
                renderCell: (params) => {
                  const statusColors = {
                    "in-progress": "secondary",
                    open: "info",
                    closed: "success",
                  };
                  return (
                    <Chip
                      label={params.value}
                      color={statusColors[params.row?.status?.toLowerCase()]}
                      size="small"
                    />
                  );
                },
              },
              {
                field: "priority",
                headerName: "Priority",
                flex: 1,
                renderCell: (params) => {
                  return params.row?.priority || "";
                },
              },
              {
                field: "createdBy",
                headerName: "CreatedBy",
                flex: 1,
                renderCell: (params) => {
                  return params.row?.createdBy || "";
                },
              },
              {
                field: "createdOn",
                headerName: "Created On",
                flex: 1,
                renderCell: (params) =>
                  format(new Date(params.value), "dd-MM-yyyy"),
              },
              {
                field: "modifiedOn",
                headerName: "Last Modified On",
                flex: 1,
                renderCell: (params) =>
                  format(new Date(params.value), "dd-MM-yyyy"),
              },
            ]}
            paginationMode="server"
            totalElements={totalElements}
            paramsData={{
              page: paginationData.page,
              pageSize: paginationData.rowsPerPage,
            }}
            setPaginationModel={setPaginationParams}
          />
        )}
      </div>
    </>
  );
}

export default ReportBuilder;
